<template>
  <div id="app" :style="cssVars">
    <div
      v-if="showInactiveTeacherBanner"
      class="container-fluid expired-banner"
    >
      <div class="row h-100 align-items-center">
        <div class="col-auto pl-lg-4 pr-0">
          <img
            class="logo-image"
            src="/assets/hey-marvelous-icon-white-coral.svg"
          />
        </div>
        <div class="col">
          <span class="d-none d-md-inline-flex">
            {{ $t('GENERAL.TEXT.YOUR_ACCOUNT_HAS_EXPIRED') }}
          </span>
          <span class="d-md-none">
            {{ $t('GENERAL.TEXT.YOUR_ACCOUNT_HAS_EXPIRED_MOBILE') }}
          </span>
        </div>
        <div class="col-auto">
          <router-link
            class="undecorated-router-link c-coral"
            :to="{ name: 'settings-adjust-plan' }"
          >
            <span class="d-none d-md-inline-block mr-2">
              {{ $t('GENERAL.LINK.REACTIVATE_PLAN') }}
            </span>
          </router-link>
        </div>
      </div>
    </div>
    <router-view
      name="sidebar"
      class="sidebar-container"
      :class="{ 'expired-banner__height': showInactiveTeacherBanner }"
      v-if="!$route.meta.hideAdminSidebar"
    />
    <div
      class="content-container"
      :class="{ 'expired-banner__height': showInactiveTeacherBanner }"
      :style="{ backgroundImage: `url(${backgroundImageURL})` }"
    >
      <router-view
        name="topbar"
        class="topbar"
        v-if="!$route.meta.hideTopBar"
      />
      <router-view class="main" />
      <router-view name="footer" />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { setMixpanelUTMParameters } from '@/web/services/analytics';
import { captureTrackingParams } from '@/web/utils/tracking';
import { hex2rgba } from '@/core/api/utils/color-converter.ts';
import HMDialog from '@/web/components/common/HMDialog';
import { getConfig } from '@/web/utils/configuration';
import { isStandalone } from '@/web/utils/detect-device';

export default Vue.extend({
  name: 'App',

  computed: {
    ...mapGetters({
      isPWA: 'globals/isPWA',
      studio: 'studio/currentStudio',
      showRefreshModal: 'globals/showRefreshModal',
      backgroundImage: 'globals/backgroundImage',
      affiliateCodeFromStore: 'globals/affiliateCode',
    }),
    ...mapGetters('user', ['teacher']),
    backgroundImageURL() {
      return (
        this.backgroundImage?.storage_url ??
        this.backgroundImage?.embedded_image_url
      );
    },
    studioDefaultFont() {
      if (!this.studio?.default_font) return '';
      return this.studio?.default_font.replaceAll('+', ' ');
    },
    cssVars() {
      if (this.$route.meta?.isStudio) {
        return {
          '--brand-color': this.studio?.primary_color,
          '--primary-color': this.studio?.primary_color,
          '--brand-font': this.studioDefaultFont,
          '--second-font': this.studioDefaultFont,
          '--brand-color-with-opacity-05': hex2rgba(
            this.studio?.primary_color,
            0.5
          ),
          '--brand-color-with-opacity-01': hex2rgba(
            this.studio?.primary_color,
            0.1
          ),
          '--brand-color-with-opacity-03': hex2rgba(
            this.studio?.primary_color,
            0.3
          ),
        };
      } else {
        return {};
      }
    },
    showInactiveTeacherBanner() {
      return (
        !this.$route.meta?.isStudio && this.teacher && this.teacher.is_expired
      );
    },
    applicationName() {
      return getConfig().applicationName;
    },
  },
  methods: {
    async checkAffiliateParameter() {
      let affiliateCode = this.$route.query.ref;
      const storedAffiliateRef = this.$cookie.get('ref');
      console.log('Affiliate code in URL: ', affiliateCode);
      console.log('Affiliate code in cookie: ', storedAffiliateRef);
      if (affiliateCode && affiliateCode.startsWith('delete')) {
        console.log('Deleting any existing affiliate code');
        this.$cookie.delete('ref');
        this.$store.dispatch('globals/setAffiliateCode', null);
        affiliateCode = null;
      } else {
        if (affiliateCode && !storedAffiliateRef) {
          console.log(
            'No affiliate code present yet. Setting ref cookie to ' +
              affiliateCode
          );
          const now = new Date();
          const exp = new Date(
            now.getFullYear(),
            now.getMonth() + 1,
            now.getDate()
          );
          this.$cookie.set('ref', affiliateCode, {
            expires: exp,
          });
          this.$store.dispatch('globals/setAffiliateCode', affiliateCode);
        } else if (storedAffiliateRef) {
          console.log(
            'Affiliate code present already: ' +
              storedAffiliateRef +
              '. Will not overwrite old ref!'
          );
          this.$store.dispatch('globals/setAffiliateCode', storedAffiliateRef);
        }
        if (this.affiliateCodeFromStore) {
          const affiliateDiscount = await this.$store.dispatch(
            'affiliates/trackAffiliateLinkClick',
            {
              code: this.affiliateCodeFromStore,
              valid:
                !storedAffiliateRef || storedAffiliateRef === affiliateCode,
            }
          );
          this.$store.dispatch(
            'affiliates/setAffiliateCoupon',
            affiliateDiscount
          );
        }
      }
    },
    checkPWAParm() {
      if (this.isPWA) return;
      const isPWA = this.$route.query.source === 'pwa';
      if (isPWA || isStandalone()) {
        this.$store.dispatch('globals/setIsPWA', true);
      }
    },
    checkPreviewTemplateParam() {
      if (this.$route.query.preview_template) {
        this.$store.dispatch(
          'globals/setPreviewTemplate',
          this.$route.query.preview_template
        );
      }
    },
  },
  watch: {
    studio: {
      handler(studio) {
        if (this.$route.meta?.isStudio) {
          if (studio) {
            const html = document.querySelector('html');
            html.style.setProperty('--brand-color', this.studio?.primary_color);
            html.style.setProperty('--brand-font', this.studioDefaultFont);
            html.style.setProperty(
              '--brand-color-with-opacity-05',
              hex2rgba(this.studio?.primary_color, 0.5)
            );
            html.style.setProperty(
              '--brand-color-with-opacity-02',
              hex2rgba(this.studio?.primary_color, 0.3)
            );
            html.style.setProperty(
              '--brand-color-with-opacity-03',
              hex2rgba(this.studio?.primary_color, 0.3)
            );
            html.style.setProperty(
              '--brand-color-with-opacity-01',
              hex2rgba(this.studio?.primary_color, 0.1)
            );
          }
        } else {
          const html = document.querySelector('html');
          html.style.setProperty('--brand-color', null);
          html.style.setProperty('--brand-font', null);
          html.style.setProperty('--brand-color-with-opacity-05', null);
          html.style.setProperty('--brand-color-with-opacity-01', null);
          html.style.setProperty('--brand-color-with-opacity-03', null);
        }
      },
      immediate: true,
    },
    $route: {
      handler(route) {
        // make sure brand-color var is only set in student facing pages
        if (!route.meta?.isStudio) {
          const html = document.querySelector('html');
          html.style.setProperty('--brand-color', null);
          html.style.setProperty('--brand-font', null);
          html.style.setProperty('--brand-color-with-opacity-05', null);
          html.style.setProperty('--brand-color-with-opacity-01', null);
        }
      },
      immediate: true,
    },
    '$route.query.source': {
      handler(source) {
        this.checkPWAParm();
      },
      immediate: true,
    },
    '$route.query.ref': {
      handler(ref) {
        this.checkAffiliateParameter();
      },
      immediate: true,
    },
    '$route.query.preview_template': {
      handler(_) {
        this.checkPreviewTemplateParam();
      },
      immediate: true,
    },
    showRefreshModal: {
      handler(showRefreshModal) {
        if (showRefreshModal) {
          if (this.$route.meta.preventRefreshModal) return;

          const applicationName = this.applicationName;
          this.$modal.show(
            HMDialog,
            {
              title: this.$t('GENERAL.TITLE.NEW_RELEASE'),
              subtitle: this.$t('GENERAL.TEXT.PLEASE_REFRESH'),
              text: this.$t('GENERAL.TEXT.WE_HAVE_RELEASED_A_NEW_VERSION', {
                applicationName,
              }),
              confirmButton: this.$t('GENERAL.BUTTON.RELOAD'),
              discardButton: this.$t('GENERAL.BUTTON.CLOSE'),
              confirm: () => {
                window.location.reload();
              },
              discard: () => {
                this.$store.dispatch('globals/setShowRefreshModal', false, {
                  root: false,
                });
                this.$emit('close');
              },
            },
            {
              clickToClose: false,
            }
          );
        }
      },
    },
  },
  mounted() {
    setMixpanelUTMParameters(this.$route.query);
    captureTrackingParams();
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

#app {
  display: flex;
  min-height: 100vh;
}

.content-container {
  display: flex;
  height: 100%;
  min-height: 100vh;
  justify-content: center;
  flex-direction: column;
  flex-wrap: nowrap;
  flex-grow: 1;
  width: 100%;
}

.sidebar-container {
  height: 100vh;
  position: sticky;
  top: 0;
}

.main {
  flex: 1;
}

$expiredBannerHeight: 50px;
.expired-banner {
  position: fixed;
  left: 0;
  top: 0;
  z-index: $zindex-fixed;
  width: 100%;
  background-color: $charcoal;
  color: $white;
  height: $expiredBannerHeight;

  &__height {
    padding-top: $expiredBannerHeight;
  }
}
</style>
